import React from 'react';
import { PageToolbarProps } from './types';
import { Button, ButtonProps, Menu, MenuItem } from '@companycam/slab-web';
import styled from 'styled-components';
import { translate } from 'javascripts/translations/i18n';

type TextFormat = 'heading1' | 'heading2' | 'heading3' | 'paragraph';

export type FormatMenuProps = {
  editor: PageToolbarProps['editor'];
};

export type FormatButtonProps = ButtonProps & {
  $format: TextFormat;
};

const StyledMenu = styled(Menu)`
  margin-top: var(--cc_size_spacing_xs);
  margin-bottom: var(--cc_size_spacing_xs);
`;

const StyledFormatButton = styled(Button)`
  &.toolbar-format-trigger {
    width: 136px;
    overflow: hidden;
  }

  &.toolbar-format-trigger > span {
    font-weight: ${({ $format }: FormatButtonProps) =>
      $format === 'paragraph' ? 400 : 600};
    width: 75px;
    text-align: left;
  }
`;

const StyledHeading1MenuItem = styled.span`
  color: var(--Text-Default, var(--cc_color_text_default));
  font-family: Averta;
  font-size: var(--cc_size_text_xl);
  font-style: normal;
  font-weight: 700;
`;

const StyledHeading2MenuItem = styled.span`
  color: var(--Text-Default, var(--cc_color_text_default));
  font-family: Averta;
  font-size: var(--cc_size_text_l);
  font-style: normal;
  font-weight: 700;
`;

const StyledHeading3MenuItem = styled.span`
  color: var(--Text-Default, var(--cc_color_text_default));
  font-family: Averta;
  font-size: var(--cc_size_text_m);
  font-style: normal;
  font-weight: 700;
`;

const StyledParagraphMenuItem = styled.span`
  color: var(--Text-Default, var(--cc_color_text_default));
  font-family: Averta;
  font-size: var(--cc_size_text_m);
  font-style: normal;
  font-weight: 400;
`;

export function FormatMenu(props: FormatMenuProps) {
  const { editor } = props;

  let activeFormat: TextFormat = 'paragraph';

  if (editor.isActive('heading', { level: 1 })) {
    activeFormat = 'heading1';
  } else if (editor.isActive('heading', { level: 2 })) {
    activeFormat = 'heading2';
  } else if (editor.isActive('heading', { level: 3 })) {
    activeFormat = 'heading3';
  }

  const handleSelectParagraph = () => {
    editor.chain().focus().setParagraph().run();
  };

  const handleSelectHeading1 = () => {
    editor.chain().focus().toggleHeading({ level: 1 }).run();
  };

  const handleSelectHeading2 = () => {
    editor.chain().focus().toggleHeading({ level: 2 }).run();
  };

  const handleSelectHeading3 = () => {
    editor.chain().focus().toggleHeading({ level: 3 }).run();
  };

  return (
    <StyledMenu
      placement="top-start"
      triggerButton={
        <StyledFormatButton
          className="toolbar-format-trigger"
          data-testid="pages__toolbar-format-menu-trigger"
          $format={activeFormat}
          design="minimal"
          icon={{ name: 'chevron-down', position: 'after' }}
        >
          {translate(`pages.toolbar.${activeFormat}`)}
        </StyledFormatButton>
      }
    >
      <MenuItem
        data-testid="page-modal__toolbar-menu-item-h1"
        onClick={handleSelectHeading1}
      >
        <StyledHeading1MenuItem>
          {translate('pages.toolbar.heading1')}
        </StyledHeading1MenuItem>
      </MenuItem>
      <MenuItem
        data-testid="page-modal__toolbar-menu-item-h2"
        onClick={handleSelectHeading2}
      >
        <StyledHeading2MenuItem>
          {translate('pages.toolbar.heading2')}
        </StyledHeading2MenuItem>
      </MenuItem>
      <MenuItem
        data-testid="page-modal__toolbar-menu-item-h3"
        onClick={handleSelectHeading3}
      >
        <StyledHeading3MenuItem>
          {translate('pages.toolbar.heading3')}
        </StyledHeading3MenuItem>
      </MenuItem>
      <MenuItem
        data-testid="page-modal__toolbar-menu-item-p"
        onClick={handleSelectParagraph}
      >
        <StyledParagraphMenuItem>
          {translate('pages.toolbar.paragraph')}
        </StyledParagraphMenuItem>
      </MenuItem>
    </StyledMenu>
  );
}
